/**
 * @file     AcademyIcons.tsx
 * @readme   ../README.md
 * @purpose  Custom SVG vector icons for the academy, theme-aware, light/dark compatible. Replaces all emoji usage.
 */
'use client';

import { useAppTheme } from '@/core/theme';

interface IconProps {
  size?: number;
  className?: string;
}

/* ── Status icons for journey nodes ──────────────────────── */

export function IconLocked({ size = 20 }: IconProps) {
  const { theme } = useAppTheme();
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="5" y="11" width="14" height="10" rx="2" stroke={theme.semanticColors.textMuted} strokeWidth="1.5" fill="none" />
      <path d="M8 11V7a4 4 0 1 1 8 0v4" stroke={theme.semanticColors.textMuted} strokeWidth="1.5" strokeLinecap="round" fill="none" />
      <circle cx="12" cy="16" r="1.5" fill={theme.semanticColors.textMuted} />
    </svg>
  );
}

export function IconAvailable({ size = 20 }: IconProps) {
  const { theme } = useAppTheme();
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="9" stroke={theme.semanticColors.interactivePrimary} strokeWidth="1.5" fill="none" />
      <circle cx="12" cy="12" r="3" fill={theme.semanticColors.interactivePrimary} opacity="0.3" />
    </svg>
  );
}

export function IconStarted({ size = 20 }: IconProps) {
  const { theme } = useAppTheme();
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="9" stroke={theme.semanticColors.interactivePrimary} strokeWidth="1.5" fill="none" />
      <path d="M12 3a9 9 0 0 1 0 18" fill={theme.semanticColors.interactivePrimary} opacity="0.25" />
      <circle cx="12" cy="12" r="3" fill={theme.semanticColors.interactivePrimary} />
    </svg>
  );
}

export function IconCompleted({ size = 20 }: IconProps) {
  const { theme } = useAppTheme();
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="9" fill={theme.semanticColors.statusSuccess} opacity="0.15" stroke={theme.semanticColors.statusSuccess} strokeWidth="1.5" />
      <path d="M8 12.5l2.5 2.5L16 9.5" stroke={theme.semanticColors.statusSuccess} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

/* ── Audience icons ──────────────────────────────────────── */

export function IconBuyer({ size = 40 }: IconProps) {
  const { theme } = useAppTheme();
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="8" y="22" width="32" height="20" rx="2" stroke={theme.semanticColors.interactivePrimary} strokeWidth="2" fill="none" />
      <path d="M4 24l20-16 20 16" stroke={theme.semanticColors.interactivePrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <rect x="18" y="30" width="12" height="12" rx="1" stroke={theme.semanticColors.interactivePrimary} strokeWidth="1.5" fill={theme.semanticColors.interactivePrimary} opacity="0.12" />
      <path d="M24 30v12" stroke={theme.semanticColors.interactivePrimary} strokeWidth="1" />
      <circle cx="36" cy="12" r="6" stroke={theme.semanticColors.statusSuccess} strokeWidth="1.5" fill={theme.semanticColors.statusSuccess} opacity="0.12" />
      <path d="M33.5 12l1.5 1.5 3.5-3.5" stroke={theme.semanticColors.statusSuccess} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

export function IconSeller({ size = 40 }: IconProps) {
  const { theme } = useAppTheme();
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="8" y="22" width="32" height="20" rx="2" stroke={theme.semanticColors.interactivePrimary} strokeWidth="2" fill="none" />
      <path d="M4 24l20-16 20 16" stroke={theme.semanticColors.interactivePrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <rect x="18" y="30" width="12" height="12" rx="1" stroke={theme.semanticColors.interactivePrimary} strokeWidth="1.5" fill={theme.semanticColors.interactivePrimary} opacity="0.12" />
      <circle cx="36" cy="14" r="7" stroke={theme.semanticColors.statusWarning} strokeWidth="1.5" fill={theme.semanticColors.statusWarning} opacity="0.12" />
      <text x="36" y="18" textAnchor="middle" fontSize="12" fontWeight="600" fill={theme.semanticColors.statusWarning}>$</text>
    </svg>
  );
}

export function IconLandlord({ size = 40 }: IconProps) {
  const { theme } = useAppTheme();
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="6" y="16" width="36" height="26" rx="2" stroke={theme.semanticColors.interactivePrimary} strokeWidth="2" fill="none" />
      <path d="M6 22h36" stroke={theme.semanticColors.interactivePrimary} strokeWidth="1.5" />
      <rect x="12" y="26" width="8" height="8" rx="1" stroke={theme.semanticColors.interactivePrimary} strokeWidth="1.5" fill={theme.semanticColors.interactivePrimary} opacity="0.12" />
      <rect x="28" y="26" width="8" height="8" rx="1" stroke={theme.semanticColors.interactivePrimary} strokeWidth="1.5" fill={theme.semanticColors.interactivePrimary} opacity="0.12" />
      <path d="M24 6v10" stroke={theme.semanticColors.interactivePrimary} strokeWidth="2" strokeLinecap="round" />
      <circle cx="24" cy="10" r="4" stroke={theme.semanticColors.interactivePrimary} strokeWidth="1.5" fill="none" />
      <path d="M22 10h4" stroke={theme.semanticColors.interactivePrimary} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M24 8v4" stroke={theme.semanticColors.interactivePrimary} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/* ── Gamification icons ──────────────────────────────────── */

export function IconStreak({ size = 20 }: IconProps) {
  const { theme } = useAppTheme();
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2c0 5-4 7-4 12a6 6 0 0 0 12 0c0-5-4-7-4-12-1 2-3 3-4 0z" fill={theme.semanticColors.statusWarning} opacity="0.2" stroke={theme.semanticColors.statusWarning} strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M12 14c0 1.5-1 2.5-1 4a2.5 2.5 0 0 0 5 0c0-1.5-1-2.5-1-4-.5 1-1.5 1-3 0z" fill={theme.semanticColors.statusWarning} opacity="0.5" />
    </svg>
  );
}

export function IconTrophy({ size = 28 }: IconProps) {
  const { theme } = useAppTheme();
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 2h8v8a4 4 0 1 1-8 0V2z" fill={theme.semanticColors.statusWarning} opacity="0.15" stroke={theme.semanticColors.statusWarning} strokeWidth="1.5" />
      <path d="M8 4H5a2 2 0 0 0-2 2v1a3 3 0 0 0 3 3h2" stroke={theme.semanticColors.statusWarning} strokeWidth="1.5" fill="none" />
      <path d="M16 4h3a2 2 0 0 1 2 2v1a3 3 0 0 1-3 3h-2" stroke={theme.semanticColors.statusWarning} strokeWidth="1.5" fill="none" />
      <path d="M12 14v3" stroke={theme.semanticColors.statusWarning} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M8 20h8" stroke={theme.semanticColors.statusWarning} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M9 17h6v3H9z" fill={theme.semanticColors.statusWarning} opacity="0.15" />
    </svg>
  );
}

export function IconBadgeLocked({ size = 28 }: IconProps) {
  const { theme } = useAppTheme();
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="10" r="7" stroke={theme.semanticColors.borderSubtle} strokeWidth="1.5" fill="none" strokeDasharray="3 2" />
      <path d="M12 17l-2 5 2-1.5 2 1.5-2-5z" stroke={theme.semanticColors.borderSubtle} strokeWidth="1" fill="none" />
      <rect x="9.5" y="8" width="5" height="4" rx="0.5" stroke={theme.semanticColors.borderSubtle} strokeWidth="1" fill="none" />
      <path d="M10.5 8V6.5a1.5 1.5 0 1 1 3 0V8" stroke={theme.semanticColors.borderSubtle} strokeWidth="1" fill="none" />
    </svg>
  );
}

export function IconLockLarge({ size = 48 }: IconProps) {
  const { theme } = useAppTheme();
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="10" y="22" width="28" height="20" rx="4" stroke={theme.semanticColors.textMuted} strokeWidth="2" fill={theme.semanticColors.textMuted} opacity="0.06" />
      <path d="M16 22v-6a8 8 0 1 1 16 0v6" stroke={theme.semanticColors.textMuted} strokeWidth="2" strokeLinecap="round" fill="none" />
      <circle cx="24" cy="32" r="3" fill={theme.semanticColors.textMuted} />
      <path d="M24 35v3" stroke={theme.semanticColors.textMuted} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export function IconPractice({ size = 48 }: IconProps) {
  const { theme } = useAppTheme();
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="16" stroke={theme.semanticColors.interactivePrimary} strokeWidth="2" fill={theme.semanticColors.interactivePrimary} opacity="0.08" />
      <path d="M24 12v12l8 4" stroke={theme.semanticColors.interactivePrimary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M16 36l-4 6M32 36l4 6" stroke={theme.semanticColors.interactivePrimary} strokeWidth="2" strokeLinecap="round" fill="none" />
    </svg>
  );
}

/* ── Chevron icons ───────────────────────────────────────── */

export function IconChevronUp({ size = 12 }: IconProps) {
  const { theme } = useAppTheme();
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2.5 7.5L6 4l3.5 3.5" stroke={theme.semanticColors.textMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function IconChevronDown({ size = 12 }: IconProps) {
  const { theme } = useAppTheme();
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2.5 4.5L6 8l3.5-3.5" stroke={theme.semanticColors.textMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ── Correct / Incorrect feedback ────────────────────────── */

export function IconCorrect({ size = 16 }: IconProps) {
  const { theme } = useAppTheme();
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="7" fill={theme.semanticColors.statusSuccess} opacity="0.15" stroke={theme.semanticColors.statusSuccess} strokeWidth="1" />
      <path d="M5 8.5l2 2 4-4" stroke={theme.semanticColors.statusSuccess} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function IconIncorrect({ size = 16 }: IconProps) {
  const { theme } = useAppTheme();
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="7" fill={theme.semanticColors.statusError} opacity="0.15" stroke={theme.semanticColors.statusError} strokeWidth="1" />
      <path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke={theme.semanticColors.statusError} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
